import React, {
  useLayoutEffect,
  useRef
} from 'react';
import { createPortal } from 'react-dom';

import './confetti.css';

const PARTICLE_COUNT = 220;
const BASE_SPEED = 80;
const SPEED_VARIANCE = 920;
const GRAVITY = 780;
const DRAG = 0.35;

type ParticleType = {
  xPos: number;
  yPos: number;
  xVel: number;
  yVel: number;
  rot: number;
  rotVel: number;
  size: number;
  delay: number;
  color: string;
  el?: HTMLSpanElement;
}

type ConfettiProps = {
  colors: string[];
}

const pickColor = (colors: string[], index: number): string => {
  const n = colors.length;
  if (n === 0) {
    return 'var(--pink)';
  }
  if (n === 1) {
    return colors[0];
  }
  const roll = (index * 17) % 100;
  if (roll < 60) {
    return colors[0];
  }
  if (roll < 85) {
    return colors[1];
  }
  return colors[2] ?? colors[1];
};

const spawnParticle = (colors: string[], index: number): ParticleType => {
  const fountain = Math.random() < 0.72;
  const angle = fountain
    ? -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.35
    : Math.random() * 2 * Math.PI;
  const speed = BASE_SPEED + Math.random() * SPEED_VARIANCE;
  const tilt = (Math.random() - 0.5) * 180;

  return {
    xPos: (Math.random() - 0.5) * 120,
    yPos: (Math.random() - 0.5) * 48,
    xVel: speed * Math.cos(angle) + tilt,
    yVel: speed * Math.sin(angle),
    rot: Math.random() * 360,
    rotVel: (Math.random() - 0.5) * 720,
    size: 1.1 + Math.random() * 2.3,
    delay: Math.random() * 220,
    color: pickColor(colors, index)
  };
};

export const Confetti = ({ colors }: ConfettiProps) => {
  const particles = useRef<ParticleType[]>(undefined);
  const prevTime = useRef<number | null>(null);
  const requestRef = useRef<number | null>(null);

  if (!particles.current) {
    particles.current = [];
    while (particles.current.length < PARTICLE_COUNT) {
      particles.current.push(spawnParticle(colors, particles.current.length));
    }
  }

  const animate = (timestamp: number) => {
    if (!prevTime.current) {
      prevTime.current = timestamp;
    }
    const elapsed = timestamp - prevTime.current;
    prevTime.current = timestamp;
    const dt = elapsed / 1000;

    for (let i = 0; i < (particles.current?.length ?? 0); i++) {
      const p = particles.current![i];
      p.delay -= elapsed;
      if (p.delay > 0) {
        if (p.el) {
          p.el.style.transform =
            `translate(${p.xPos}px, ${p.yPos}px) rotate(${p.rot}deg)`;
        }
        continue;
      }

      p.xPos += p.xVel * dt;
      p.yPos += p.yVel * dt;
      p.yVel += GRAVITY * dt;
      p.xVel *= (1 - DRAG * dt);
      p.rot += p.rotVel * dt;
      p.rotVel *= (1 - 0.18 * dt);

      if (p.el) {
        p.el.style.transform =
          `translate(${p.xPos}px, ${p.yPos}px) rotate(${p.rot}deg)`;
      }
    }

    requestRef.current = requestAnimationFrame(animate);
  };

  useLayoutEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [particles]);

  return createPortal(
    <div className='confettiContainer'>
      {particles.current?.map((p, i) => (
        <span
          key={'particle_' + i}
          className='particle'
          style={{
            color: p.color,
            fontSize: `${p.size}rem`,
            transform: `translate(${p.xPos}px, ${p.yPos}px) rotate(${p.rot}deg)`
          }}
          ref={(el) => { p.el = el ?? undefined; }}>♥</span>
      ))}
    </div>,
    document.body
  );
};
